import React, { useEffect, useMemo, useState } from 'react'
import propTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import Select from 'react-select'
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Tooltip,
	Typography,
	makeStyles,
	useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/AddCircleOutline'
import { loadCategories, createCategory } from '../../../../store/contentAssetCategories/contentAssetCategoriesActions'
import { buildReactSelectStyles } from '../wizardFieldStyles'

const useStyles = makeStyles((theme) => ({
	wrapper: {
		marginBottom: theme.spacing(2)
	},
	label: {
		fontSize: '0.75rem',
		marginBottom: theme.spacing(0.5)
	},
	error: {
		marginTop: theme.spacing(0.5)
	},
	selectRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	selectField: {
		flex: 1,
		minWidth: 0
	},
	dialogParentLabel: {
		fontSize: '0.75rem',
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(0.5)
	}
}))

const TOP_LEVEL_OPTION = { value: null, label: '(None — top-level)' }

/**
 * Builds the display label for a category by joining its parent chain with " › ".
 */
const buildHierarchicalLabel = (category, allById) => {
	const chain = []
	let cursor = category
	const seen = new Set()
	while (cursor && !seen.has(cursor.Id)) {
		seen.add(cursor.Id)
		chain.unshift(cursor.Name)
		const parentId = cursor.ParentId
		cursor = parentId ? allById[parentId] : null
	}
	return chain.join(' › ')
}

const CategoryDropdown = ({ value, error, onChange }) => {
	const classes = useStyles()
	const theme = useTheme()
	const dispatch = useDispatch()
	const categories = useSelector((state) => state.contentAssetCategories.get('categories').toJS())
	const isLoading = useSelector((state) => state.contentAssetCategories.get('isLoading'))
	const isCreating = useSelector((state) => state.contentAssetCategories.get('isCreating'))
	const [createError, setCreateError] = useState(null)
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [dialogName, setDialogName] = useState('')
	const [dialogParentId, setDialogParentId] = useState(null)
	const [dialogError, setDialogError] = useState(null)

	useEffect(() => {
		dispatch(loadCategories())
	}, [dispatch])

	const options = useMemo(() => {
		const byId = categories.reduce((acc, c) => ({ ...acc, [c.Id]: c }), {})
		return categories.map((c) => ({
			value: c.Id,
			label: buildHierarchicalLabel(c, byId)
		}))
	}, [categories])

	// Only top-level categories can be parents (2-level cap enforced server-side).
	const parentOptions = useMemo(
		() => [TOP_LEVEL_OPTION, ...categories.filter((c) => c.ParentId == null).map((c) => ({ value: c.Id, label: c.Name }))],
		[categories]
	)

	const selected = options.find((o) => o.value === value) || null

	const handleChange = (opt) => {
		setCreateError(null)
		onChange(opt && typeof opt.value === 'number' ? opt.value : null)
	}

	const handleCreate = async (rawInput) => {
		setCreateError(null)
		try {
			const category = await dispatch(createCategory(rawInput))
			if (category && typeof category.Id === 'number') {
				onChange(category.Id)
			}
		} catch (err) {
			setCreateError('Could not create category. Try again.')
		}
	}

	const openDialog = () => {
		setDialogName('')
		setDialogParentId(null)
		setDialogError(null)
		setIsDialogOpen(true)
	}
	const closeDialog = () => {
		setIsDialogOpen(false)
	}
	const handleDialogSubmit = async () => {
		const trimmed = (dialogName || '').trim()
		if (!trimmed) {
			setDialogError('Name is required.')
			return
		}
		setDialogError(null)
		try {
			const category = await dispatch(createCategory(trimmed, dialogParentId))
			if (category && typeof category.Id === 'number') {
				onChange(category.Id)
			}
			setIsDialogOpen(false)
		} catch (err) {
			setDialogError('Could not create category. Try again.')
		}
	}

	return (
		<div className={classes.wrapper}>
			<Typography className={classes.label}>Category</Typography>
			<div className={classes.selectRow}>
				<div className={classes.selectField}>
					<CreatableSelect
						placeholder='Select or type to create...'
						options={options}
						value={selected}
						onChange={handleChange}
						onCreateOption={handleCreate}
						isLoading={isLoading || isCreating}
						isClearable={false}
						styles={buildReactSelectStyles(theme, Boolean(error))}
						menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
					/>
				</div>
				<Tooltip title='New category (with optional parent)'>
					<IconButton size='small' onClick={openDialog} aria-label='new category'>
						<AddIcon />
					</IconButton>
				</Tooltip>
			</div>
			{error && (
				<Typography variant='caption' color='error' className={classes.error}>
					{error}
				</Typography>
			)}
			{createError && (
				<Typography variant='caption' color='error' className={classes.error}>
					{createError}
				</Typography>
			)}

			<Dialog open={isDialogOpen} onClose={closeDialog} maxWidth='xs' fullWidth>
				<DialogTitle>New category</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						fullWidth
						label='Name'
						value={dialogName}
						onChange={(e) => setDialogName(e.target.value)}
						margin='dense'
					/>
					<Typography className={classes.dialogParentLabel}>Parent (optional)</Typography>
					<Select
						options={parentOptions}
						value={parentOptions.find((o) => o.value === dialogParentId) || TOP_LEVEL_OPTION}
						onChange={(opt) => setDialogParentId(opt && typeof opt.value === 'number' ? opt.value : null)}
						isClearable={false}
						styles={buildReactSelectStyles(theme, false)}
						menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
					/>
					{dialogError && (
						<Typography variant='caption' color='error' className={classes.error}>
							{dialogError}
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDialog} disabled={isCreating}>Cancel</Button>
					<Button onClick={handleDialogSubmit} color='primary' variant='contained' disabled={isCreating}>
						Create
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	)
}

CategoryDropdown.propTypes = {
	value: propTypes.number,
	error: propTypes.string,
	onChange: propTypes.func.isRequired
}

CategoryDropdown.defaultProps = {
	value: null,
	error: null
}

export default CategoryDropdown
